/**
 * The sheet's matrix — what the plan PRESCRIBES, cycle by cycle, and the one place a trainer pins a
 * cell against the rule.
 *
 * ── It draws the PLAN, under the rule being authored ────────────────────────────────────────
 * The exercise it is handed is `stagedProjection`'s: the stored node's rows, roles and pins with the
 * staged rule map laid over them, on the column the sheet's chips have selected. Until 15 September
 * 2026 it drew the stored rule alone and a note above it said so — „+10 kg" moved no number on the
 * grid until the sheet had been closed, which is a preview that previews nothing.
 *
 * ── The VIEW arrives built ──────────────────────────────────────────────────────────────────
 * `ProgressionSheet` needs the same answer for the caption's column, the key beside it and the
 * phase note, and it was calling `progressionMatrixView` a second time to get them — two builds of
 * one grid per render, and two places for a change to the input to be made in only one.
 *
 * ── Behaviour comes from `lib/matrixGridInteraction` and `shared/lib/useGridFocus` ───────────
 * One tab stop for the whole grid, arrows moving the focused cell, Enter opening its field, Escape
 * contained so it cancels the cell rather than the sheet, Delete handing a pin back. That module
 * exists because this behaviour was written twice, verbatim, and a focus fix landed in one copy.
 *
 * ── A pin is its own draft write ────────────────────────────────────────────────────────────
 * `setMatrixOverride` is the app's ONE pin writer: it refuses the authored base column, and clears
 * rather than pins a value equal to what the rule already computes. It is told which COLUMN the cell
 * was drawn in — the same one this grid was built on — and nothing here re-implements any of it.
 */

import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

import {
  revertMatrixOverride,
  setMatrixBaseValue,
  setMatrixOverride,
  type ExerciseDraft,
} from '~/entities/plan-draft';
import type { MeasurementFieldKey } from '~/shared/measurement';

import { useGridFocus } from '~/shared/lib/useGridFocus';

import { MatrixCellEditor, matrixCellKeyDown } from '../lib/matrixGridInteraction';
import type {
  MatrixCellView,
  ProgressionMatrixView,
  Translate,
} from '../model/matrixCellState';

import styles from './SheetMatrix.module.css';
import { TypeMenu } from './TypeMenu';

const KEY = 'web:planBuilder.progression';

/**
 * Which STEP of the mock's stagger a cell is on: `column + row * ⅔`, in whole steps.
 *
 * The milliseconds are the stylesheet's — `--dur-cell-stagger-*` — because a duration literal in a
 * component is a duration nothing else can find. The mock's own ratio is `0.03` per column against
 * `0.02` per row, which is two thirds, and the CAP is applied in CSS so a 52-cycle phase does not
 * leave its last cell 1,6s behind its first.
 */
function foldStep(row: number, column: number): number {
  return column * 3 + row * 2;
}

interface SheetMatrixProps {
  /** The one view the sheet built — see the header. */
  readonly view: ProgressionMatrixView;
  /** The exercise the grid writes INTO: the pin's slot token and the set-type menu's rows. */
  readonly exercise: ExerciseDraft;
  /** The column the grid is drawn on, and therefore the one a pin lands in. */
  readonly field: MeasurementFieldKey;
  /** The exercise's first active cycle, which `setMatrixOverride` refuses a pin on. */
  readonly baseCycle: number;
  /**
   * The grid gained or lost focus. The foot's „⏎ zapisz · Esc oddaj regule" names the CELL field's
   * two keys, so it is printed while the grid holds focus and nowhere else.
   */
  readonly onFocusWithinChange: (isFocused: boolean) => void;
  /** The day dock's live region. A cell that refuses the editor says why through it. */
  readonly announce: (message: string) => void;
  readonly t: Translate;
}

export function SheetMatrix({
  view,
  exercise,
  field,
  baseCycle,
  onFocusWithinChange,
  announce,
  t,
}: SheetMatrixProps) {
  const gridRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const nowHeadRef = useRef<HTMLSpanElement>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const navigation = useGridFocus({
    gridRef,
    rowCount: view.rows.length,
    // The row HEADER is column 0 and every cycle follows it, so ArrowLeft off the first cycle
    // reaches the set-type menu without the grid growing a second tab stop.
    columnCount: view.columns.length + 1,
    disabled: editing !== null,
  });

  /* THE CLIENT IS ON SCREEN BEFORE THE FIRST PAINT SETTLES. A phase wider than the pane opens at
   * `scrollLeft: 0`, and on a 12-cycle fork the cycle the client is standing in — with „TU JEST
   * KLIENT" over it, which is the one thing the live variant exists to say — sat 42px past the
   * scroller's right edge, measured 15 September 2026. Nobody scrolls a grid they have not been told
   * has more in it.
   *
   * ONE BOX IS MOVED, and it is this one. `scrollIntoView` was what put the column here, and it walks
   * EVERY scrollable ancestor: measured 15 September 2026, opening the sheet slid the day form behind
   * it ~17px sideways and back, which is the jump this app reports as a defect on the same footing as
   * a wrong number. The arithmetic below is the `inline: 'center'` it replaces, resolved against this
   * scroller alone — `offsetLeft` is measured from `.grid`, whose inline start IS this box's scroll
   * origin, so no ancestor is read and none is written.
   *
   * In a LAYOUT effect, and assigned rather than animated: the column is simply in view on the first
   * painted frame, never seen travelling there. */
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    const head = nowHeadRef.current;
    if (scroller === null || head === null) return;
    const centred = head.offsetLeft + head.offsetWidth / 2 - scroller.clientWidth / 2;
    scroller.scrollLeft = Math.max(0, centred);
  }, [view.nowColumn?.position]);

  return (
    /* The grid FITS OR SCROLLS. Its cycle tracks have a floor, so a 30-cycle phase divides the pane
     * past the width of „26,5 kg" and this box is what carries the overflow — the alternative is a
     * grid that silently clips every cell's number. */
    <div
      ref={scrollerRef}
      className={styles.scroller}
      /* The room the „TU JEST KLIENT" tag stands in, reserved before the first frame rather than
       * grown into: this box clips, and the tag hangs above the column heads. */
      data-live={view.isLive ? 'true' : undefined}
    >
      <div
        ref={gridRef}
        role="grid"
        aria-label={t('web:planBuilder.progression.sheet.matrix.a11y')}
        aria-rowcount={view.rows.length + 1}
        aria-colcount={view.columns.length + 1}
        className={styles.grid}
        style={{ '--prog-cycles': String(view.columns.length) } as CSSProperties}
        /* CAPTURE, because the row header is a Base UI menu trigger and Base UI opens a menu on
         * ArrowDown. See `shared/lib/useGridFocus`'s own note. */
        onKeyDownCapture={navigation.onKeyDown}
        onFocus={() => {
          onFocusWithinChange(true);
        }}
        onBlur={(event) => {
          // `relatedTarget` is where focus WENT. Moving between two cells keeps it inside the grid,
          // and a blur fired by the cell field's own unmount reports null rather than an escape.
          const next = event.relatedTarget;
          if (next instanceof Node && event.currentTarget.contains(next)) return;
          onFocusWithinChange(false);
        }}
      >
        <div role="row" style={{ display: 'contents' }}>
          <span
            role="columnheader"
            className={styles.corner}
            aria-label={t(`${KEY}.matrix.rowHeader`)}
          />
          {view.columns.map((column) => (
            <span
              key={column.cycleIndex}
              ref={column.isNow ? nowHeadRef : undefined}
              role="columnheader"
              className={styles.cycleHead}
              /* The mock's own inverted head on the cycle the client is standing in — white box,
                * dark number. It is the second place „TERAZ" is said, and neither is the only one:
                * the sub-line under the number carries the word. */
              data-now={column.isNow ? 'true' : undefined}
            >
              <span className={styles.cycleLabel}>{column.label}</span>
              {column.subLabel !== null && (
                <span className={styles.cycleTag}>{column.subLabel}</span>
              )}
              {/* ZADANE | WYKONANE, once per lived column, over the two halves of every cell under it. */}
              {column.halves !== null && (
                <span className={styles.halfHeads} aria-hidden="true">
                  <span>{column.halves.prescribed}</span>
                  <span>{column.halves.performed}</span>
                </span>
              )}
            </span>
          ))}
        </div>

        {/* „TU JEST KLIENT" — the mock's `nowLineC` and `nowTagC`, placed on the TRACK rather than
          * at a computed offset. The mock has to write `calc(56px + 8px + col * (NOW − 1) + …)`
          * because its outline is absolutely positioned over the grid; ours is a grid item spanning
          * every row of the column it names, so the two can never drift apart when a track resizes
          * or the scroller moves.
          *
          * `role="presentation"` with `aria-hidden` keeps the grid's children rows and nothing else:
          * the column head already says „TERAZ" in words, so this is a drawing of the same fact. */}
        {view.nowColumn !== null && (
          <span
            role="presentation"
            aria-hidden="true"
            className={styles.nowMarker}
            /* The track, and the whole height of it. `grid-row: 1 / -1` cannot say that here: the
              * rows are `display: contents`, so every row line is IMPLICIT and `-1` resolves back to
              * line 1 — the marker then collapsed to one cell and auto-placement pushed the first
              * data row a whole column sideways. The span is data (one head row plus the set rows),
              * so it is stated where the data is. */
            style={{
              /* BOTH ends of both axes, stated. An absolutely positioned grid item takes the grid
                * container's padding edge for any line left `auto`, so `grid-column-start` alone
                * stretched the outline from the open cycle to the end of the phase — and it still
                * LOOKED right, because its left edge was in the right place. `plan-progression-live`
                * measures the centre for exactly that reason. */
              gridColumn: `${String(view.nowColumn.position + 2)} / span 1`,
              gridRow: `1 / span ${String(view.rows.length + 1)}`,
            }}
          >
            {/* Centred over its column, except at the two ends — see `nowColumn.align`. The tag is
              * wider than a cycle track, and what hangs off the scroller's START cannot be scrolled
              * back to. */}
            <span className={styles.nowTag} data-align={view.nowColumn.align}>
              {view.nowColumn.tag}
            </span>
          </span>
        )}

        {view.rows.map((row, rowPosition) => (
          <div key={row.setIndex} role="row" style={{ display: 'contents' }}>
            {/* The row's HEADER, and the set-type menu behind it — one element, because the mock
              * puts the role on the label and a separate trigger would be a tab stop per row. */}
            <TypeMenu
              exercise={exercise}
              setIndex={row.setIndex}
              badge={row.badge}
              rowLabel={row.label}
              hasFocus={navigation.focus.row === rowPosition && navigation.focus.column === 0}
              gridPosition={`${String(rowPosition)}-0`}
              foldStep={foldStep(rowPosition, 0)}
              onFocus={() => {
                navigation.onCellFocus(rowPosition, 0);
              }}
              t={t}
            />
            {row.cells.map((cell, columnPosition) => (
              <Cell
                key={cell.key}
                cell={cell}
                row={rowPosition}
                column={columnPosition + 1}
                hasFocus={
                  navigation.focus.row === rowPosition &&
                  navigation.focus.column === columnPosition + 1
                }
                isEditing={editing === cell.key}
                exerciseSlotToken={exercise.slotToken}
                field={field}
                baseCycle={baseCycle}
                onEdit={() => {
                  setEditing(cell.key);
                }}
                onClose={() => {
                  setEditing(null);
                  navigation.focusCell(rowPosition, columnPosition + 1);
                }}
                onFocus={() => {
                  navigation.onCellFocus(rowPosition, columnPosition + 1);
                }}
                announce={announce}
                t={t}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * The cross that hands a pin back — drawn, at `PencilGlyph`'s stroke, and deliberately the quietest
 * mark in the cell.
 *
 * It was a filled pink disc carrying a text „✕", hung outside the cell's corner: a second saturated
 * mark on a cell the manual state already colours, and the loudest thing on a grid whose whole job
 * is to be read as numbers. The mock draws NO badge on its pinned cell at all (the character does
 * not appear anywhere in `Builder planu - modal - progresja.dc.html`), so there is no canon to
 * reproduce here and the brief is the owner's: neutral ink, no fill, no colour, low emphasis until
 * the pointer or the keyboard is on the cell.
 *
 * 24-unit box at `PencilGlyph`'s 1.8 stroke and round caps, so the two glyphs in this slice are one
 * hand. The box is sized by the stylesheet; nothing about the drawing is a literal there.
 */
function CrossGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M7 7l10 10M17 7L7 17"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Which way a logged number moved against what was asked, drawn at the sub-line's size in
 * `CrossGlyph`'s hand. Nothing on an exact hit: the line's green already says it (owner, 15 September
 * 2026). It never carries the verdict, whose colour is the line's; see `LoggedLineMark`.
 */
function DirectionGlyph({ direction }: { readonly direction: 'above' | 'hit' | 'below' | null }) {
  if (direction === null || direction === 'hit') return null;
  return (
    <svg
      className={styles.direction}
      data-direction={direction}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d={direction === 'above' ? 'M12 20V5M6 11l6-6 6 6' : 'M12 4v15M6 13l6 6 6-6'}
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface CellProps {
  readonly cell: MatrixCellView;
  readonly row: number;
  readonly column: number;
  readonly hasFocus: boolean;
  readonly isEditing: boolean;
  readonly exerciseSlotToken: string;
  readonly field: MeasurementFieldKey;
  readonly baseCycle: number;
  readonly onEdit: () => void;
  readonly onClose: () => void;
  readonly onFocus: () => void;
  readonly announce: (message: string) => void;
  readonly t: Translate;
}

function Cell({
  cell,
  row,
  column,
  hasFocus,
  isEditing,
  exerciseSlotToken,
  field,
  baseCycle,
  onEdit,
  onClose,
  onFocus,
  announce,
  t,
}: CellProps) {
  const isPinned = cell.state === 'manual';

  const commit = (raw: number | null) => {
    /* TWO WRITES, and which one it is, is what column the cell stands in. C1 is the number every
     * other cycle is projected FROM, so editing it moves the row's own prescription (the same value
     * the set table shows) and the whole row follows; every later column is a per-cycle pin against
     * the rule. Both operations live in `entities/plan-draft` and this only picks between them. */
    const address = { setIndex: cell.setIndex, cycleIndex: cell.cycleIndex, baseCycle, field };
    if (cell.isBase) setMatrixBaseValue(exerciseSlotToken, address, raw);
    else setMatrixOverride(exerciseSlotToken, address, raw);
    onClose();
  };

  const revert = () => {
    revertMatrixOverride(exerciseSlotToken, {
      setIndex: cell.setIndex,
      cycleIndex: cell.cycleIndex,
    });
  };

  return (
    <span
      role="gridcell"
      className={styles.cell}
      data-cell={`${String(row)}-${String(column)}`}
      data-state={cell.state}
      /* The cell being TYPED INTO, marked in the accent for as long as the field is open. The mock
       * pulses its open cell; the colour is the owner's call of 15 September 2026, and it is the one
       * moment a cell is about the trainer rather than about the plan. It is not a sixth state:
       * `data-state` still carries what the projection says, so Escape restores the cell by taking
       * this attribute away and Enter leaves „ręczne" standing underneath it. */
      data-editing={isEditing ? 'true' : undefined}
      data-editable={cell.isEditable ? 'true' : undefined}
      data-locked={cell.isLocked ? 'true' : undefined}
      /* How the cell is drawn — green, red or neither. The whole decision is `matrixCellState`'s
       * (`LoggedCellView.tone`), because it composes four facts and this is exactly the kind of
       * assembly a component gets subtly wrong. Never the only channel: the ✗ marks the short row
       * and the cell's accessible name says which column held it back. */
      data-lived={cell.logged?.tone}
      style={{ '--fold-step': String(foldStep(row, column)) } as CSSProperties}
      /* ONE tab stop for the whole grid. 52 cycles by 20 rows is 1040 presses to reach the foot. */
      tabIndex={hasFocus ? 0 : -1}
      aria-label={cell.ariaLabel}
      aria-readonly={cell.isEditable ? undefined : true}
      onFocus={onFocus}
      /* THE WHOLE BOX, and a refused cell answers the press. The hit target has always been the
       * cell rather than the number — measured 15 September 2026, a click 3px inside the corner of a
       * 109 by 33 cell opens its field — but a press on a cell that takes no editor did nothing at
       * all: no field, no sound, no reason, which reads as a grid that is simply not clickable. That
       * is the same defect Enter had, and it is answered the same way, through the sheet's own live
       * region. */
      onClick={() => {
        if (isEditing) return;
        if (cell.isEditable) onEdit();
        else if (cell.editRefusal !== null) announce(cell.editRefusal);
      }}
      onKeyDown={(event) => {
        matrixCellKeyDown(event, {
          isEditable: cell.isEditable,
          isEditing,
          isOverridden: isPinned,
          onEdit,
          onRevert: revert,
          onRefused: () => {
            if (cell.editRefusal !== null) announce(cell.editRefusal);
          },
        });
      }}
    >
      {cellBody()}
    </span>
  );

  /**
   * WHAT STANDS IN THE CELL: the lived split, the open field, or the plan's own number. Three
   * readings and the order is the rule — a consumed cycle is drawn as what happened even while the
   * grid has a field open somewhere else.
   */
  function cellBody(): ReactNode {
    /* THE SPLIT CELL: two halves side by side and one line between them, what the plan froze onto
     * this row when the client started the workout on the left, what they did on the right. Both
     * halves are `aria-hidden`: the cell's own accessible name reads the pair as one sentence and
     * names the column that fell short, and four texts inside one gridcell is the same fact read out
     * four times. The owner's spec of 15 September 2026; see `LoggedCellView`. Each half holds the
     * regular cell's two lines at the regular cell's two sizes, so the box is the unlived one. */
    if (cell.logged !== null) {
      const { prescribed, performed, word, tone, valueMark, restMark, rowShortMark } = cell.logged;
      return (
        <>
          <span className={styles.half} data-side="prescribed" aria-hidden="true" title={prescribed.row}>
            <span className={styles.halfValue}>{prescribed.value ?? prescribed.rest}</span>
            {prescribed.value !== null && prescribed.rest !== null && (
              <span className={styles.halfRest}>{prescribed.rest}</span>
            )}
          </span>
          <span
            className={styles.half}
            data-side="performed"
            aria-hidden="true"
            title={performed?.row ?? word ?? undefined}
          >
            {tone === 'missed' && <span className={styles.missedMark}>✗</span>}
            {tone !== 'missed' && performed === null && (
              <span className={styles.halfWord}>{word}</span>
            )}
            {performed !== null && (
              <>
                <span
                  className={styles.halfValue}
                  data-short={valueMark?.isShort === true ? 'true' : undefined}
                >
                  {performed.value ?? performed.rest}
                  <DirectionGlyph direction={valueMark?.direction ?? null} />
                  {rowShortMark && <span className={styles.verdictMark}>✗</span>}
                </span>
                {performed.value !== null && performed.rest !== null && (
                  <span
                    className={styles.halfRest}
                    data-short={restMark?.isShort === true ? 'true' : undefined}
                  >
                    {performed.rest}
                    <DirectionGlyph direction={restMark?.direction ?? null} />
                  </span>
                )}
              </>
            )}
          </span>
        </>
      );
    }

    if (isEditing) {
      return (
        <MatrixCellEditor
          valueRaw={cell.editValueRaw}
          scale={cell.editScale}
          className={String(styles.editor)}
          ariaLabel={t(`${KEY}.cell.edit`, { set: cell.setIndex, cycle: cell.cycleIndex })}
          onCommit={commit}
          onCancel={onClose}
        />
      );
    }

    return (
      <>
        <span className={styles.value}>{cell.value}</span>
        {cell.annotation !== null && <span className={styles.annotation}>{cell.annotation}</span>}
        {isPinned && (
          <button
            type="button"
            className={styles.revert}
            /* Not a tab stop: a column of pins on a 52-cycle phase would be 52 extra stops. The
             * keyboard reverts with Delete on the cell, which this name says out loud. */
            tabIndex={-1}
            aria-label={t(`${KEY}.cell.revert`, { set: cell.setIndex, cycle: cell.cycleIndex })}
            onClick={(event) => {
              event.stopPropagation();
              revert();
            }}
          >
            <CrossGlyph />
          </button>
        )}
      </>
    );
  }
}
